// src/components/CarrierDefaultBanner.js
//
// Record 599 (Bob, 2026-10-07; task list section 597): the 'default claim' watch on the filed-claim screen.
// It draws and decides nothing: src\data\carrierDefault.js decides (defaultStatus / bannerText / defaultLetter),
// from the claim's own presentment, its recorded carrier acts (offProperty.claimActs) and the agreement's
// carrierDefault profile (claimRequirements.js). No profile, no presentment, an answer on record -> nothing shows.
//
// Shown to the officers and the claimant (the caller decides `show`). The letter is a DRAFT the officer adopts;
// the platform sends nothing.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';   // Record 603: the header is loaded the way LetterGroupPanel loads it
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { CARRIER_CONTACTS } from '../data/carrierContacts';
import { claimCommittee } from '../data/claimPayload';
import { defaultStatus, bannerText, defaultLetter } from '../data/carrierDefault';
import { claimActs } from '../data/offProperty';
import { getClaimRequirements } from '../data/claimRequirements';

export default function CarrierDefaultBanner({ claim, show, user }) {
  const [letter, setLetter] = useState(null);
  const [copied, setCopied] = useState(false);
  const [ctx, setCtx] = useState(null);   // Record 603: offices / terms / seats / carrier contacts / letterhead
  const status = useMemo(
    () => defaultStatus(claim, claimActs(claim), getClaimRequirements(claim && claim.applicableAgreement), Date.now()),
    [claim]);
  // Record 603 (Record 599 open item: the letter lacked a letterhead load). Loaded as LetterGroupPanel loads it: the
  // committee's offices, terms and seats, the carrier contacts of record, and the committee's letterhead (ruling 229).
  // Officers only, and only once the limit has expired (the letter exists only then). A claimant's copy, or a failed
  // read, keeps the [NOT ON FILE] blocks and NOT SENDABLE. No sign-off (ruling 231) is offered here: this letter
  // carries its own DRAFT head and foot, which affixSignoff does not strip - the officer signs "Respectfully," himself.
  const committee = claimCommittee(claim);
  const scopeKey = claim && claim.scopeKey;
  const officer = !!user && (user.isAdmin || user.role === 'general_chair' || user.role === 'local_chair');
  const expired = status.state === 'expired';
  useEffect(() => {
    if (!show || !officer || !expired || !committee) return undefined;
    let dead = false;
    (async () => {
      try {
        const ids = [committee].concat(scopeKey ? [scopeKey] : []);
        const [offs, terms, cmte] = await Promise.all([
          Promise.all(ids.map(id => getDoc(doc(db, 'offices', id)))),
          Promise.all(ids.map(id => getDocs(query(collection(db, 'officeTerms'), where('officeId', '==', id))))),
          getDoc(doc(db, 'committees', committee)),
        ]);
        if (dead) return;
        setCtx({
          offices: offs.filter(s => s.exists()).map(s => ({ officeId: s.id, ...s.data() })),
          terms: terms.flatMap(snap => snap.docs.map(d => ({ termId: d.id, ...d.data() }))),
          seats: cmte.exists() && Array.isArray(cmte.data().seats) ? cmte.data().seats : [],
          carrierContacts: CARRIER_CONTACTS,
          letterhead: cmte.exists() ? (cmte.data().letterhead || null) : null,
        });
      } catch (e) { /* the header stays unresolved; the letter says NOT SENDABLE */ }
    })();
    return () => { dead = true; };
  }, [show, officer, expired, committee, scopeKey]);
  if (!show || !claim) return null;
  if (status.state === 'running') {
    return (
      <div style={{ fontSize: 12, color: '#8AA4BF', margin: '0 0 12px 2px' }}>{bannerText(status)}</div>
    );
  }
  if (status.state !== 'expired') return null;
  return (
    <div style={{ background: '#2A0F0F', border: '1px solid #E06C6C', borderRadius: 6, padding: '12px 14px', marginBottom: 14, lineHeight: 1.6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#E06C6C' }}>{bannerText(status)}</div>
      <div style={{ fontSize: 12, color: '#DCE7F2', marginTop: 6 }}>
        {/* Record 612 (decision 28): the agreement's words are inside the one wording above; the date is said here. */}
        Carrier time limit expired on {status.due}{status.extendedOn ? ' (as extended on ' + status.extendedOn + ')' : ''}.
        <div style={{ fontSize: 10, color: '#8AA4BF', fontFamily: 'monospace' }}>{status.source}</div>
        {status.note ? <div style={{ fontSize: 11, color: '#FFD27A', marginTop: 4 }}>{status.note}</div> : null}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}
              onClick={() => { setLetter(defaultLetter(claim, status, ctx ? { ...ctx, asOf: claim.filedAt || claim.createdAt } : undefined)); setCopied(false); }}>Prepare default letter</button>
      {letter && letter.ok && (
        <div style={{ marginTop: 10 }}>
          {!letter.sendable && (
            <div style={{ fontSize: 11, color: '#FFD27A', marginBottom: 6 }}>
              NOT SENDABLE as it stands: {[].concat(letter.missingFields, letter.unresolvedBlocks).filter(Boolean).join(', ')} not on file. Fill these in before it goes to the carrier.
            </div>
          )}
          <textarea aria-label="The letter as it will be sent (read only)" readOnly className="form-input" style={{ width: '100%', minHeight: 320, fontFamily: 'monospace', fontSize: 11 }} value={letter.text} />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
                  onClick={async () => { try { await navigator.clipboard.writeText(letter.text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* clipboard unavailable */ } }}>
            {copied ? 'Copied' : 'Copy letter'}
          </button>
        </div>
      )}
    </div>
  );
}
